import React from 'react'
import { DialogTitle, DialogContent, Grid, DialogActions, Button } from '@mui/material'
import { Web } from '@mui/icons-material'
import { FaLinkedin, FaGithub, FaDribbble } from 'react-icons/fa'
import { useForm, useMutationBasedFormSubmitHandlers } from 'hooks/form'
import { graphql, useFragment } from 'react-relay'
import ResponsiveDialog from 'components/ResponsiveDialog'
import TextField from 'components/TextField'
import { SocialLinksEditDialog_UpdateUserMutation } from '__generated__/SocialLinksEditDialog_UpdateUserMutation.graphql'
import { SocialLinksEditDialog_User$key } from '__generated__/SocialLinksEditDialog_User.graphql'
import FormSubmitButton from 'components/FormSubmitButton'
import Form from 'components/Form'

export interface ISocialLinksEditDialogProps {
  user: SocialLinksEditDialog_User$key
  onClose: () => void
  onResumeUploadOpen: () => void
}

interface IFormFields {
  url_blog: string
  url_github: string
  url_linkedin: string
  url_dribbble: string
}

function SocialLinksEditDialog(props: ISocialLinksEditDialogProps) {
  const { user: userFragmentRef, onClose, onResumeUploadOpen } = props

  const user = useFragment(graphql`
    fragment SocialLinksEditDialog_User on User {
      profile {
        urlBlog
        urlDribbble
        urlGithub
        urlLinkedin
      }
    }
  `, userFragmentRef)

  const form = useForm<IFormFields, SocialLinksEditDialog_UpdateUserMutation>({
    defaultValues: {
      url_blog: user.profile?.urlBlog || '',
      url_github: user.profile?.urlGithub || '',
      url_linkedin: user.profile?.urlLinkedin || '',
      url_dribbble: user.profile?.urlDribbble || '',
    },
    submitHandlers: useMutationBasedFormSubmitHandlers(graphql`
      mutation SocialLinksEditDialog_UpdateUserMutation($input: UpdateUserInput!) {
        updateUser(input: $input) {
          user {
            profile {
              urlBlog
              urlDribbble
              urlGithub
              urlLinkedin
            }
          }
        }
      }
    `, (values: IFormFields) => ({
      input: {
        profile: {
          urlBlog: values.url_blog,
          urlDribbble: values.url_dribbble,
          urlGithub: values.url_github,
          urlLinkedin: values.url_linkedin,
        },
      },
    }), {
      successMessage: 'Social links updated',
      verifySuccess: (result) => {
        return Boolean(result?.updateUser?.user?.profile)
      },
    }),
  })

  const urlBlogField = form.useFormField('url_blog', 'typing')
  const urlGithubField = form.useFormField('url_github', 'typing')
  const urlLinkedinField = form.useFormField('url_linkedin', 'typing')
  const urlDribbbleField = form.useFormField('url_dribbble', 'typing')

  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="sm">
      <Form form={form}>
        <DialogTitle>
          Social Links
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                name="url_linkedin"
                label="LinkedIn"
                input={urlLinkedinField.input}
                meta={urlLinkedinField.meta}
                fullWidth
                startAdornment={<FaLinkedin />}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                name="url_github"
                label="Github"
                input={urlGithubField.input}
                meta={urlGithubField.meta}
                fullWidth
                startAdornment={<FaGithub />}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                name="url_blog"
                label="Your Personal Website or Blog"
                input={urlBlogField.input}
                meta={urlBlogField.meta}
                fullWidth
                startAdornment={<Web />}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                name="url_dribbble"
                label="Dribbble"
                input={urlDribbbleField.input}
                meta={urlDribbbleField.meta}
                fullWidth
                startAdornment={<FaDribbble />}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <Button color="secondary" fullWidth onClick={onResumeUploadOpen}>
                Upload CV
              </Button>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>

          <FormSubmitButton />
        </DialogActions>
      </Form>
    </ResponsiveDialog>
  )
}

export default React.memo(SocialLinksEditDialog)
